import {
  default as MuiRadio,
  type RadioProps as MuiRadioProps,
} from '@mui/material/Radio';
import {
  default as MuiFormControlLabel,
  type FormControlLabelProps,
} from '@mui/material/FormControlLabel';
import { type CSSObject } from '@emotion/react';
import {
  useRadioButtonBaseStyles,
  useRadioButtonLabelStyles,
} from './radioButton.styles';
import { RadioUnchecked, RadioChecked } from './icons';
import { BodyS } from '../../../../typography';
import { AriaProps } from '../../../../types';

import type { JSX } from 'react';

export type RadioButtonProps = AriaProps &
  Pick<
    MuiRadioProps,
    'checked' | 'disabled' | 'inputRef' | 'inputProps' | 'onChange'
  > & {
    label?: FormControlLabelProps['label'];
    labelPlacement?: FormControlLabelProps['labelPlacement'];
    className?: string;
    value: string;
    tabIndex?: number;
    labelSx?: FormControlLabelProps['sx'];
    radioSx?: CSSObject;
  };

export const RadioButton = ({
  label,
  value,
  labelPlacement = 'end',
  disabled = false,
  inputRef,
  inputProps,
  onChange,
  className = '',
  tabIndex = 0,
  labelSx,
  checked,
  radioSx = {},
  ...rest
}: RadioButtonProps): JSX.Element => {
  const baseStyles = useRadioButtonBaseStyles();
  const labelStyles = useRadioButtonLabelStyles();

  const control = (
    <MuiRadio
      {...rest}
      disabled={disabled}
      disableRipple={true}
      inputRef={inputRef}
      inputProps={inputProps}
      className={className}
      tabIndex={tabIndex}
      value={value}
      size='small'
      checked={checked}
      onChange={onChange}
      icon={<RadioUnchecked />}
      checkedIcon={<RadioChecked />}
      sx={[baseStyles, radioSx]}
    />
  );

  if (!Boolean(label)) {
    return control;
  }

  return (
    <MuiFormControlLabel
      control={control}
      label={<BodyS>{label}</BodyS>}
      labelPlacement={labelPlacement}
      disabled={disabled}
      sx={{
        ...labelStyles,
        ...labelSx,
      }}
    />
  );
};
